<script setup>
// ---------------------------------------------------------------------------
// The football clock against the hockey clock, as paired figures.
//
// The Office stops its clock whenever proceedings are suspended; the calendar
// does not. Both numbers are true and they are far apart, so both are shown,
// side by side, with the difference spelled out as its own figure — that gap is
// the reason the page exists.
//
// The Office's figures are quoted from its annual reports and arrive as a prop,
// year by year. No column in this dataset carries a stopped clock, and none
// could.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { narrowRows, outsideFilters, timers, unpackFilter } from '../cases.js'
import { filters } from '../store.js'
import { useCases } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    // Pipe-separated, four of them, in the order the Office reports them.
    metricLabels: { type: String, default: '' },
    // Four rows separated by |, each a list of `year:days` as the Office
    // published them. A year missing from a row is a year it published nothing.
    quoted: { type: String, default: '' },
    // What stands in the box for a year the Office never reported on.
    missingLabel: { type: String, default: '' },
    // The years are years of issue, ours are years of legal force. Said out
    // loud only when a year is actually picked, because only then does it bite.
    yearNote: { type: String, default: '' },
    unitDays: { type: String, default: '' },
    labelStopped: { type: String, default: '' },
    labelElapsed: { type: String, default: '' },
    iconStopped: { type: String, default: '' },
    iconElapsed: { type: String, default: '' },
    fieldStopped: { type: String, default: '' },  // rink, under the Office's figures
    fieldElapsed: { type: String, default: '' },  // pitch, under the calendar's
    gapLabel: { type: String, default: '' },
    heroLabel: { type: String, default: '' },
    // How many decisions the headline rests on, {pocet} for the number. Set
    // right under the figure and quietly: it is the size of the base, and a
    // reader who wants it will look there before reading the sentence.
    heroBase: { type: String, default: '' },
    introLabel: { type: String, default: '' },
    baseLabel: { type: String, default: '' },
    // Shown instead, once a year or a sector leaves the published side behind.
    narrowedLabel: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useCases(props.src)

// The headline figure is ours alone, so it follows the whole filter.
const data = computed(() => (rows.value.length ? timers(visible.value) : null))

const base = computed(() =>
    props.heroBase.replace('{pocet}', number(data.value.hero.counted))
)

// The four rows are not. Each is defined against a figure the Office publishes,
// and two of the slicers would break them:
//
//   * the first row counts orders as zero days — that is what makes it lower
//     than the second — so filtering orders out left the two rows identical;
//   * the published figures cover the whole caseload, court cases included.
//
// So the rows narrow only as far as an outside source can follow: year and
// sector. Beyond that the pairing has no basis at all, which is what `paired`
// is about.
// Orders belong in the first row, which counts them as zero days — so this one
// narrows by year and sector only and keeps them, unlike everything else here.
const ours = computed(() => timers(narrowRows(rows.value, outsideFilters(filters))))

// A sector has no published counterpart — the Office reports for itself as a
// whole. A year does: the annual reports carry every row year by year, so the
// pairing survives it.
//
// Where the pairing breaks the columns stay and go quiet. Taking them away
// would leave a table of our own figures with nothing to be measured against,
// which is not what this picture is for.
const muted = computed(() => Boolean(filters.obor))

// `2020:38.8,2021:26.5|2023:42.4` -> [{2020: 38.8, 2021: 26.5}, {2023: 42.4}]
const published = computed(() =>
    props.quoted.split('|').map((row) =>
        Object.fromEntries(
            row
                .split(',')
                .filter(Boolean)
                .map((pair) => pair.split(':'))
                .map(([year, days]) => [year, Number(days)])
        )
    )
)

/**
 * What the Office published for the years in view, as one figure.
 *
 * The unweighted mean over the years, which is also how the all-years figure
 * has always been arrived at — so narrowing to a year changes which years are
 * averaged, never the rule. Null when it published nothing for any of them.
 */
function quotedFor(row) {
    if (muted.value) return null

    const picked = unpackFilter(filters.rok)
    const years = picked.length ? picked.filter((year) => year in row) : Object.keys(row)

    if (!years.length) return null

    return years.reduce((total, year) => total + row[year], 0) / years.length
}

const metrics = computed(() => {
    const labels = props.metricLabels.split('|')

    return [ours.value.overall, ours.value.firstInstance, ours.value.onRequest, ours.value.appeal]
        .map((stat, index) => {
            // Rounded here rather than on the way out, so the difference is the
            // subtraction the reader can do on screen: 43 - 28 has to be 15.
            const quoted = quotedFor(published.value[index] ?? {})
            const stopped = quoted === null ? null : Math.round(quoted)

            return {
                label: labels[index] ?? '',
                stopped,
                elapsed: stat.days,
                gap: stopped === null ? null : stat.days - stopped,
                counted: stat.counted
            }
        })
})

// Each box is a window onto one field that runs the height of the column, so the
// slice inside it is as tall as every box plus the gaps between them, pulled up
// by the boxes above. The percentages resolve against the box, which is why this
// needs no measuring: the gap is the same custom property the rows are spaced by.
const slice = (index, field) => ({
    backgroundImage: `url(${field})`,
    height: `calc(${metrics.value.length} * 100% + ${metrics.value.length - 1} * var(--timers-gap))`,
    top: `calc(${-index} * (100% + var(--timers-gap)))`
})

// Whole days throughout. The Office publishes to one decimal, but a tenth of a
// day is below what either clock can honestly resolve and it made the table
// harder to read across.
const number = (value) => value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })
const signed = (value) => (value > 0 ? `+${number(value)}` : number(value))
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <div v-else-if="data" class="timers">
        <div class="claim">
            <p class="stat statbox statbox--hero">
                <span class="stat__value">{{ number(data.hero.days) }}</span>
                <span class="stat__unit">{{ unitDays }}</span>
                <span v-if="heroBase" class="stat__base">{{ base }}</span>
                <span class="stat__label">{{ heroLabel }}</span>
            </p>

            <p class="lead claim__intro">{{ introLabel }}</p>
        </div>

        <!-- Each clock is a metaphor, so its column stands on the field it takes
             its name from — one rink, one pitch, each drawn once for the whole
             column. The boxes are the only windows onto it, so the lines are
             never seen outside them. -->
        <div class="timers__table" :class="{ 'timers__table--muted': muted }">
            <div class="timers__head">
                <span></span>
                <span class="timers__clock timers__clock--stopped">
                    <img :src="iconStopped" alt="" aria-hidden="true">{{ labelStopped }}
                </span>
                <span class="timers__clock">
                    <img :src="iconElapsed" alt="" aria-hidden="true">{{ labelElapsed }}
                </span>
                <span class="timers__clock timers__clock--gap">{{ gapLabel }}</span>
            </div>

            <div v-for="(metric, index) in metrics" :key="metric.label" class="timers__row">
                <p class="timers__metric">{{ metric.label }}</p>

                <p class="stat statbox statbox--stopped">
                    <span class="statbox__field" aria-hidden="true"
                          :style="slice(index, fieldStopped)"></span>

                    <template v-if="metric.stopped !== null">
                        <span class="stat__value">{{ number(metric.stopped) }}</span>
                        <span class="stat__unit">{{ unitDays }}</span>
                    </template>

                    <span v-else-if="!muted" class="statbox__missing">{{ missingLabel }}</span>
                </p>

                <p class="stat statbox statbox--elapsed">
                    <span class="statbox__field" aria-hidden="true"
                          :style="slice(index, fieldElapsed)"></span>
                    <span class="stat__value">{{ number(metric.elapsed) }}</span>
                    <span class="stat__unit">{{ unitDays }}</span>
                </p>

                <p class="stat statbox statbox--gap">
                    <template v-if="metric.gap !== null">
                        <span class="stat__value">{{ signed(metric.gap) }}</span>
                        <span class="stat__unit">{{ unitDays }}</span>
                    </template>
                </p>
            </div>
        </div>

        <div class="timers__note">
            <span aria-hidden="true"></span>
            <p class="note" :class="{ 'note--warn': muted }">
                {{ muted ? narrowedLabel : baseLabel }}
                <template v-if="!muted && filters.rok">{{ yearNote }}</template>
            </p>
        </div>
    </div>
</template>
